import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Modal from "@/components/common/Modal";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { CircleUserRound, PlusCircle } from "lucide-react";
import {
  GuestFormData,
  guestSchema,
  PreferencesData,
} from "../utils/guestSchema";
import { FormField } from "@/components/pages/reservations/guests/components/form/FormFields";
import ImageSelector from "@/components/pages/reservations/guests/components/form/ImageSelector";
import { CountryField } from "@/components/pages/reservations/guests/components/form/CountryField";
import PreferencesComponent from "@/components/pages/reservations/guests/components/form/PreferencesComponent";
import { useGuest } from "@/components/pages/reservations/guests/hooks/useGuests";

interface GuestModalProps {
  trigger: React.ReactNode;
  guestId?: string;
  onSubmit: (data: GuestFormData, photoFile?: File) => void;
}

const GuestModal: React.FC<GuestModalProps> = ({
  trigger,
  guestId,
  onSubmit,
}) => {
  const isEditing = !!guestId;

  const { data } = useGuest(guestId || "");

  const guestProfile = data?.profile;

  const [isOpen, setIsOpen] = useState(false);
  const [preferences, setPreferences] = useState<PreferencesData>(
    guestProfile?.preferences || {},
  );
  const [selectedPhotoFile, setSelectedPhotoFile] = useState<File | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<GuestFormData>({
    resolver: zodResolver(guestSchema),
    defaultValues: guestProfile
      ? {
          ...guestProfile,
          language: guestProfile.language
            ? guestProfile.language.toUpperCase()
            : "",
        }
      : {},
  });

  useEffect(() => {
    if (guestProfile?.preferences) {
      setPreferences(guestProfile.preferences);
    }
  }, [guestProfile]);

  useEffect(() => {
    if (guestProfile) {
      reset({
        ...guestProfile,
        language: guestProfile.language
          ? guestProfile.language.toUpperCase()
          : "",
      });
    }
  }, [guestProfile, reset]);

  const selectedCountry = watch("language");
  const selectedImage = watch("photoId");

  useEffect(() => {
    if (guestProfile?.preferences) {
      setPreferences(guestProfile.preferences);
    }
  }, [guestProfile]);

  const handleFormSubmit = (data: GuestFormData) => {
    const formDataWithPreferences = {
      ...data,
      preferences,
    };
    onSubmit(formDataWithPreferences, selectedPhotoFile || undefined);
    reset();
    setPreferences({});
    setSelectedPhotoFile(null);
    setIsOpen(false);
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setValue("photoId", imageUrl);
      setSelectedPhotoFile(file);
    }
  };

  return (
    <Modal
      trigger={trigger}
      title={isEditing ? "Edit Guest" : "Add New Guest"}
      description={
        isEditing ? "Update guest information" : "Enter guest details"
      }
      className="hide-scrollbar max-h-[70vh] overflow-y-auto"
      open={isOpen}
      onOpenChange={setIsOpen}
    >
      <ImageSelector
        defaultIcon={<CircleUserRound className={"size-[72px] stroke-1"} />}
        overlayContent={
          <div
            className={
              "absolute bottom-0 left-1/2 -translate-x-1/2 rounded-full bg-white"
            }
          >
            <PlusCircle className={"size-5"} />
          </div>
        }
        onImageSelect={handleImageUpload}
        selectedImage={selectedImage || undefined}
        className="mb-4"
        imageClassName={"size-[72px] rounded-full"}
      />
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
        <FormField
          id="firstName"
          label="First Name"
          register={register}
          required={true}
          error={errors.firstName?.message}
          placeholder="Enter guest first name"
        />

        <FormField
          id="lastName"
          label="Last Name"
          register={register}
          required={true}
          error={errors.lastName?.message}
          placeholder="Enter guest last name"
        />

        <FormField
          id="phone"
          label="Phone"
          type="tel"
          register={register}
          required={true}
          error={errors.phone?.message}
          placeholder="Enter phone number"
        />

        <FormField
          id="email"
          label="Email"
          type="email"
          required={true}
          register={register}
          error={errors.email?.message}
          placeholder="Enter email address"
        />

        <FormField
          id="dateOfBirth"
          label="Date of Birth"
          type="date"
          required={true}
          register={register}
          error={errors.dateOfBirth?.message}
          placeholder="Enter date of birth"
          setValue={setValue}
          watch={watch}
          formatString={"dd/MM/yyyy"}
          maxDate={new Date()}
        />

        <CountryField
          selectedCountry={selectedCountry ?? ""}
          onSelect={(code) => setValue("language", code)}
          error={errors.language?.message}
        />

        <div className="space-y-2">
          <Label>Preferences</Label>
          <PreferencesComponent
            preferences={preferences}
            setPreferences={setPreferences}
          />
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          <Button
            type="button"
            variant="new_outline"
            onClick={() => {
              if (isEditing && guestProfile) {
                reset(guestProfile);
                setPreferences(guestProfile.preferences || {});
                setSelectedPhotoFile(null);
              } else {
                reset();
                setPreferences({});
                setSelectedPhotoFile(null);
              }
            }}
          >
            Clear
          </Button>
          <Button type="submit" variant="new_default">
            {isEditing ? "Update Guest" : "Add Guest"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default GuestModal;
